/**
 * FloatingModelNode Component
 *
 * A compact pill-shaped node representing the AI model configuration
 * for an agent. Flows below the parent agent node in the cluster pattern.
 *
 * Design:
 * - Pill shape to differentiate from other node types
 * - Shows model deployment name (truncated)
 * - Clicking opens the parent agent's model configuration
 * - Connected via edge from agent's "model" handle
 */

import { memo, useCallback } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { makeStyles, tokens, mergeClasses, Tooltip } from '@coreai/fluentui-react';
import { BrainCircuitFilled, ErrorCircleFilled } from '@fluentui/react-icons';
import { useWorkflowStore } from '@/stores/workflowStore';
import { MANAGED_MODEL_REFERENCE_KEY, MANAGED_MODEL_DISPLAY_NAME } from '@/utils/connections/managedModelReference';

export type FloatingModelNodeData = {
  /** Display label (model deployment name) */
  label: string;
  /** Full model deployment path */
  modelDeployment: string;
  /** Parent agent action name */
  parentAgentName: string;
  /** Connection name for the model */
  connectionName?: string;
  /** Whether model is configured */
  isConfigured: boolean;
};

export type FloatingModelNodeType = Node<FloatingModelNodeData, 'floatingModel'>;

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },

  // Pill-shaped container — true pill via circular radius, single-line ellipsis
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusCircular,
    boxShadow: tokens.shadow2,
    cursor: 'pointer',
    maxWidth: '180px',
    transitionProperty: 'background-color, border-color, box-shadow, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,

    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
      boxShadow: tokens.shadow4,
    },

    ':active': {
      transform: 'scale(0.96)',
      boxShadow: tokens.shadow2,
    },

    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },

    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': {
        transform: 'none',
      },
    },
  },

  pillSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
  },

  pillUnconfigured: {
    borderTopColor: tokens.colorPaletteRedBorder1,
    borderRightColor: tokens.colorPaletteRedBorder1,
    borderBottomColor: tokens.colorPaletteRedBorder1,
    borderLeftColor: tokens.colorPaletteRedBorder1,
    backgroundColor: tokens.colorPaletteRedBackground1,

    ':hover': {
      backgroundColor: tokens.colorPaletteRedBackground2,
      borderTopColor: tokens.colorPaletteRedBorder2,
      borderRightColor: tokens.colorPaletteRedBorder2,
      borderBottomColor: tokens.colorPaletteRedBorder2,
      borderLeftColor: tokens.colorPaletteRedBorder2,
    },

    '@media (forced-colors: active)': {
      borderTopColor: 'LinkText',
      borderRightColor: 'LinkText',
      borderBottomColor: 'LinkText',
      borderLeftColor: 'LinkText',
    },
  },

  iconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '16px',
    height: '16px',
  },

  icon: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorBrandForeground1,
  },

  iconError: {
    color: tokens.colorPaletteRedForeground1,
  },

  label: {
    flex: 1,
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground1,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  labelUnconfigured: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
  },

  // Handle positioned at top to connect to agent
  handleTop: {
    top: '-4px !important',
  },
});

/**
 * Compact model node that appears below agent nodes.
 * Part of the agent "cluster" pattern inspired by n8n.
 */
export const FloatingModelNode = memo(
  ({ data, selected }: NodeProps<FloatingModelNodeType>) => {
    const styles = useStyles();
    const selectNodeByName = useWorkflowStore((state) => state.selectNodeByName);

    // Click opens parent agent configuration
    const handleClick = useCallback(() => {
      selectNodeByName(data.parentAgentName, 'action');
    }, [data.parentAgentName, selectNodeByName]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      },
      [handleClick]
    );

    // Resolve display: model deployment > connection name > fallback.
    // The Microsoft-managed connection is a synthetic in-memory ref (never persisted) —
    // map its internal key to the branded label so users never see the
    // raw "managedAiConnection" string (matches ConnectionErrorBanner).
    const displayConnectionName =
      data.connectionName === MANAGED_MODEL_REFERENCE_KEY
        ? MANAGED_MODEL_DISPLAY_NAME
        : data.connectionName;

    const shortName = data.isConfigured
      ? (data.modelDeployment.split('/').pop() ?? data.label)
      : (displayConnectionName ?? 'No model');

    // Accessible name reflects what shortName actually represents
    const ariaLabel = data.isConfigured
      ? `Model: ${shortName}`
      : displayConnectionName
        ? `Connection: ${displayConnectionName} (model not configured — click to set up)`
        : 'Model not configured — click to set up';

    const tooltipContent = data.isConfigured
      ? `Model: ${data.modelDeployment}${displayConnectionName ? `\nConnection: ${displayConnectionName}` : ''}`
      : displayConnectionName
        ? `Connection: ${displayConnectionName}\nModel not configured — click to set up`
        : 'Model not configured — click to set up';

    return (
      <div className={styles.wrapper}>
        <Tooltip content={tooltipContent} relationship="description" positioning="below">
          <div
            className={mergeClasses(
              styles.pill,
              selected && styles.pillSelected,
              !data.isConfigured && styles.pillUnconfigured
            )}
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          >
            <span className={styles.iconWrapper} aria-hidden="true">
              {data.isConfigured ? (
                <BrainCircuitFilled className={styles.icon} />
              ) : (
                <ErrorCircleFilled className={mergeClasses(styles.icon, styles.iconError)} />
              )}
            </span>
            <span
              className={mergeClasses(
                styles.label,
                !data.isConfigured && styles.labelUnconfigured
              )}
              title={shortName}
            >
              {shortName}
            </span>
          </div>
        </Tooltip>

        {/* Handle at top to receive connection from agent */}
        <Handle
          type="target"
          position={Position.Top}
          id="input"
          isConnectable={false}
          className={styles.handleTop}
        />
      </div>
    );
  }
);

FloatingModelNode.displayName = 'FloatingModelNode';
